import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";

// HOOKS
import { useApprovalsModalViewModel } from "../../viewModels/useApprovalsModalViewModel";
import { useApprovalDecisionsViewModel } from "../../viewModels/useApprovalDecisionsViewModel";
import useUserStore from "../../../../../../stores/useUserStore";

// COMPONENTS
import {
	BaseButton,
	LoadingMessage,
	BaseText,
	SecondaryButton,
	PrimaryButton,
} from "../../../../../../components";

// TYPES
import { BaseModalProps } from "../../../../types";

import ApprovalStageCard from "./ApprovalStageCard";
import StageHasDecisionView from "./StageHasDecisionView";
import ApprovalFlowDetailInputs from "./ApprovalFlowDetailInputs";

// UTILS
import {
	formatDateMontDayYearTime,
	getFontAwesomeIcon,
} from "../../../../../../utils";

export type FieldsAndApprovalDecisions = {
	decision: {
		uuid: string | null;
		dtDecision: string | null;
		isApproved: string | null;
		note: string | null;
	};
	assignedUser: {
		uuid: string | null;
		name: string | null;
		email: string | null;
	};
	decidedBy: {
		uuid: string | null;
		name: string | null;
		email: string | null;
	};
	isPreApproved: boolean;
	allowedRoles: string[];
	label: string;
	valueKey: string;
	saveUuid: boolean;
	prefix: string;
	prefixWeight: string;
	heading: string;
	badges: string[];
	quickActionInputs: Array<{
		label: string;
		valueKey: string;
	}>;
	inputs: Array<{
		label: string;
		valueKey: string;
		saveUuid?: boolean;
		prefix?: string;
		prefixWeight?: string;
		heading?: string;
		inputType: string;
		labelTextClasses?: string;
		inputContainerWidth?: string;
		uuid?: string;
		isVisible?: boolean;
		placeholder?: string;
		isRequired?: boolean;
		textAreaHeight?: string;
		specificInputContainerClasses?: string;
		inputClasses?: string;
		characterLimit?: number;
	}>;
};

export type FormValues = {
	[key: string]: {
		note: string;
		actionButtonSelected: string | null;
		assignedTo: {
			uuid: string;
			name: string;
		};
		decidedBy: {
			uuid: string;
			name: string;
		};
	};
};
const ApprovalFlowForm = <T,>({
	modalSelected,
	isApproveButtonEnabled,
	onClose,
	fields,
	updateRowData,
}: BaseModalProps<T>) => {
	//// HOOKS ////
	const { approvalDecisions, activeUuid } = useApprovalsModalViewModel();

	const { user } = useUserStore();
	const { isLoadingCreateDecision, createApprovalDecisionMutation } =
		useApprovalDecisionsViewModel({
			approvalDecisions,
			decisionType: modalSelected,
			fields,
			activeUuid: activeUuid as string,
			updateRowData,
			onClose,
		});

	const [loadingDataAndForm, setLoadingDataAndForm] = useState<boolean>(true);

	/////////////////////////////////////////////////////////////////// FORM SETUP ////////////////////////////////////////////////////////////////////

	const fieldsAndApprovalDecisions = React.useMemo(
		() =>
			approvalDecisions?.stages?.reduce(
				(acc, stage) => {
					const stageFields = fields?.stages?.find(
						(s: any) =>
							s.valueKey === stage.ApprovalTemplateStages.step,
					);

					acc.push({
						decision: stage.ApprovalDecisions,
						assignedUser: {
							uuid: stage.AssignedTo?.uuid,
							name: stage.AssignedTo?._name,
							email: stage.AssignedTo?.email,
						},
						decidedBy: {
							uuid: stage.DecidedBy?.uuid,
							name: stage.DecidedBy?._name,
							email: stage.DecidedBy?.email,
						},
						allowedRoles: stage.allowedRoles,

						isPreApproved: stage.isPreApproved,

						...stageFields,
					});
					return acc;
				},
				[] as Record<string, any>,
			),
		[approvalDecisions?.stages, fields?.stages],
	);

	const [selectedStage, setSelectedStage] =
		useState<FieldsAndApprovalDecisions>();

	const methods = useForm<FormValues>({
		mode: "onChange",
		defaultValues: {},
	});

	const { handleSubmit, watch, register, setValue, reset, getValues } =
		methods;

	const formValues = getValues();

	const handleApproveAllStages = () => {
		fieldsAndApprovalDecisions.forEach(
			(stage: FieldsAndApprovalDecisions) => {
				const actionButtonFieldPath =
					`${stage.valueKey}.actionButtonSelected` as const;
				setValue(actionButtonFieldPath, "1", {
					shouldDirty: true,
				});
				if (!stage.isPreApproved) {
					const fieldPath =
						`${stage.valueKey}.decidedBy.uuid` as const;
					setValue(fieldPath, user.uuid as string, {
						shouldDirty: true,
					});
				}
			},
		);
	};

	// Reset form values when data becomes available
	useEffect(() => {
		if (
			fieldsAndApprovalDecisions &&
			fieldsAndApprovalDecisions?.length > 0
		) {
			const formDefaults = fieldsAndApprovalDecisions.reduce(
				(acc: any, stage: FieldsAndApprovalDecisions) => {
					if (stage.isPreApproved) {
						acc[stage.valueKey] = {
							note: stage.decision.note || "",
							actionButtonSelected: "1",
							assignedTo: {
								uuid: stage.assignedUser?.uuid || "",
								name: stage.assignedUser?.name
									? `${stage.assignedUser.name} ${
											stage.assignedUser.email
												? ` (${stage.assignedUser.email})`
												: ""
										}`
									: "",
							},
						};
					} else if (stage.decision.isApproved === null) {
						acc[stage.valueKey] = {
							note: stage.decision.note || "",
							actionButtonSelected: null,
							assignedTo: {
								uuid: stage.assignedUser?.uuid || "",
								name: stage.assignedUser?.name
									? `${stage.assignedUser.name} ${
											stage.assignedUser.email
												? ` (${stage.assignedUser.email})`
												: ""
										}`
									: "",
							},
						};
					}
					return acc;
				},
				{} as Record<string, any>,
			);

			reset(formDefaults);
			setLoadingDataAndForm(false);
			setSelectedStage(fieldsAndApprovalDecisions[0]);
		}
	}, [fieldsAndApprovalDecisions, reset]);

	useEffect(() => {
		if (
			fieldsAndApprovalDecisions &&
			fieldsAndApprovalDecisions?.length > 0
		) {
			setLoadingDataAndForm(false);
			fieldsAndApprovalDecisions[0] &&
				setSelectedStage(fieldsAndApprovalDecisions[0]);
		}
	}, [fieldsAndApprovalDecisions]);

	//////////////////////////////////////////////////////////////////////////// ON SUBMIT  //////////////////////////////////////////////////////////////////////////////

	const onSubmit = async (formData: Record<string, any>) => {
		createApprovalDecisionMutation.mutate(formData);
	};

	//////////////////////////////////////////////////////////////////////////// CLASSNAMES //////////////////////////////////////////////////////////////////////////////

	const userBadgeClassNames =
		"bg-gray-100 rounded-full px-2 p-1 text-sm text-gray-700 mr-2 max-w-20";
	const approvalStageCardContainerClasses = `flex gap-x-4 border-1 border-navy-200 w-full rounded-md p-3 bg-white font-omnes-medium text-navy-500`;

	const selectedStageDecisionIsApprovedMessage =
		selectedStage &&
		`Approved by ${selectedStage.decidedBy.name} on ${formatDateMontDayYearTime(
			selectedStage.decision.dtDecision as string,
		)}`;

	const selectedStageDecisionIsPreApprovedMessage =
		selectedStage &&
		`Pre-approved by System on ${formatDateMontDayYearTime(
			selectedStage.decision.dtDecision as string,
		)}`;

	let showInputs = false;
	let selectedStageDecisionMessageToDisplay;
	if (
		selectedStage?.valueKey === "2" &&
		selectedStage.isPreApproved &&
		selectedStage.decision.isApproved
	) {
		showInputs = true;
	} else if (selectedStage && selectedStage.decision.isApproved === null) {
		showInputs = true;
	}

	if (
		selectedStage?.valueKey === "2" &&
		selectedStage.isPreApproved &&
		selectedStage.decision.isApproved
	) {
		selectedStageDecisionMessageToDisplay =
			selectedStageDecisionIsPreApprovedMessage;
	} else if (selectedStage && selectedStage.decision.isApproved) {
		selectedStageDecisionMessageToDisplay =
			selectedStageDecisionIsApprovedMessage;
	}

	const stageTwo = formValues && formValues["2"];
	const stageTwoHasAssignedManager = !!(
		stageTwo &&
		stageTwo.assignedTo &&
		stageTwo.assignedTo.uuid
	);

	return (
		<>
			{loadingDataAndForm || isLoadingCreateDecision ? (
				<div className="flex items-center justify-center w-full h-full ">
					<LoadingMessage message="" />
				</div>
			) : selectedStage && fieldsAndApprovalDecisions ? (
				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex flex-col w-full h-full"
					>
						<div className="w-full flex-grow flex gap-x-3 ">
							<ol className="flex w-[260px] border-r-1 border-navy-200  bg-gray-100">
								<div className="flex flex-col w-full items-start justify-start gap-y-4 pt-4 px-2">
									{fieldsAndApprovalDecisions?.map(
										(
											stage: FieldsAndApprovalDecisions,
											index: number,
										) => {
											let isStageApprovalEnabled = true;
											if (
												stage.valueKey === "1" &&
												!isApproveButtonEnabled
											) {
												isStageApprovalEnabled = false;
											}

											if (
												stageTwoHasAssignedManager &&
												stage.valueKey === "1"
											) {
												isStageApprovalEnabled = true;
											}
											return (
												<li
													key={stage.valueKey}
													className={`${approvalStageCardContainerClasses} ${
														selectedStage.valueKey ===
														stage.valueKey
															? "ring-2 ring-primary"
															: ""
													}`}
												>
													<ApprovalStageCard
														stage={stage}
														register={register}
														setSelectedStage={
															setSelectedStage
														}
														index={index}
														getValues={getValues}
														watch={watch}
														isStageApprovalEnabled={
															isStageApprovalEnabled
														}
													/>
												</li>
											);
										},
									)}
								</div>
							</ol>

							<div className="flex gap-x-4 w-3/4 rounded-md h-full pt-2.5 ">
								<div className="flex flex-col w-full">
									<div className="flex flex-col w-full gap-y-5 mb-5">
										<BaseText
											text={selectedStage.heading}
											fontFamily="font-omnes-medium"
											color="text-navy-500"
											tag="h2"
											size="text-sm"
										/>
										{selectedStage.decision.isApproved !==
											null && (
											<div className="flex items-center gap-x-2">
												<span className="text-mint-700 text-sm">
													{getFontAwesomeIcon(
														"check",
														"regular",
													)}
												</span>

												<BaseText
													size="text-sm"
													color={"text-mint-700"}
													tag={"p"}
													text={
														selectedStageDecisionMessageToDisplay
													}
													fontFamily={
														"font-omnes-medium"
													}
													additionalClasses={""}
												/>
											</div>
										)}
									</div>

									{showInputs && (
										<ApprovalFlowDetailInputs
											selectedStage={selectedStage}
											reset={reset}
											getValues={getValues}
											setValue={setValue}
											userBadgeClassNames={
												userBadgeClassNames
											}
											onClose={onClose}
											register={register}
											isApproveButtonEnabled={
												stageTwoHasAssignedManager
													? stageTwoHasAssignedManager
													: (isApproveButtonEnabled as boolean)
											}
										/>
									)}
									{!showInputs && (
										<StageHasDecisionView
											selectedStage={selectedStage}
											userBadgeClassNames={
												userBadgeClassNames
											}
										/>
									)}
								</div>
							</div>
						</div>
						<div className="flex justify-between items-center py-4 flex-shrink-0 border-t-1 border-navy-200">
							{/* this button should never be disabled according to compass/compass canada logic */}
							<BaseButton
								hoverBackground="hover:bg-mint-800 hover:border-mint-800"
								fontFamily="font-omnes-medium"
								fontSize="text-14px"
								shape="rounded-full"
								fontColor="text-white"
								buttonSize="h-26px"
								bgColor="bg-mint-700"
								borderColor="border-1 border-mint-700"
								additionalClasses="text-nowrap px-4"
								type="button"
								iconClasses={`mr-2 text-white`}
								onClick={handleApproveAllStages}
								icon={getFontAwesomeIcon(
									"checkDouble",
									"regular",
								)}
								text={fields?.approveAllStagesButton.label}
							/>

							<div className="flex gap-x-3">
								<SecondaryButton
									text="Cancel"
									onClick={onClose}
								/>
								<PrimaryButton
									onClick={handleSubmit(onSubmit)}
									disabledButtonClasses="bg-gray-400 rounded-full"
									text={fields?.confirmButton.label}
									isDisabled={!methods.formState.isDirty}
								/>
							</div>
						</div>
					</form>
				</FormProvider>
			) : null}
		</>
	);
};

export default ApprovalFlowForm;
